import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LineEnergyProduction } from '../../features/Charts/LineEnergyProduction/LineEnergyProduction';
import { WithSidebar } from '../../components/Sidebar/Sidebar';
import { ChooseScenarioCard } from '../../features/Cards/ChooseScenarioCard/ChooseScenarioCard';
import { Cnt, FirstCol, ScenarioWithExport, SecondCol, SPartnersBanner, SPartnersCnt, SWrapper } from './styles';
import { EnergyBalance } from '../../features/Charts/EnergyBalance/EnergyBalance';
import { EnergyPies } from '../../features/EnergyPies/EnergyPies';
import { SpecificInformationCard } from 'features/Cards/SpecificInformationCard/SpecificInformationCard';
import { SPageCnt } from 'components/Card/styles';
import { useTitle } from 'react-use';
import keys from 'i18n/keys';
import { useTranslation } from 'react-i18next';
import { LineEnergyProductionModal } from 'features/Charts/LineEnergyProduction/LineEnergyProductionModal';
import { URL } from 'services/consts/routes';
import { useAppDispatch, useAppSelector } from 'services/configuration/store/hooks';
import { Box, Spacer } from 'components/common';
import { Button } from 'components/Button/Button';
import { SimulatorHeader } from 'features/Simulator/SimulatorHeader/SimulatorHeader';
import { SimulatorHeader as CommonSimulatorHeader } from 'components/SimulatorHeader/SimulatorHeader';
import { SaveSimulation } from 'features/Simulator/SaveSimulation/SaveSimulation';
import { DataInputContext } from 'components/DataInput/dataInputContext';
import { SIMULATOR_INTRO_STEPS } from './TourSteps/consts';

import type { DriverStep } from 'tour/useDriverTour';
import { useDriverTour } from 'tour/useDriverTour';
import { buildTourSteps } from './TourSteps/TourSteps';
import { setShouldRunSimulatorTour } from 'services/slices/periodSlice';

export const Simulator = () => {
  const dispatch = useAppDispatch();
  const { shouldRunSimulatorTour } = useAppSelector(state => state.period);

  const [isLineChartInModal, setIsLineChartInModal] = useState(false);
  const { t, i18n } = useTranslation();

  useTitle(t(keys.PAGE_TITLE_SIMULATOR));

  // Steps rebuilt on language change (used on next start)
  const steps: DriverStep[] = useMemo(() => buildTourSteps(t), [t, i18n.language]);

  const { start: startTour, stop: stopTour } = useDriverTour(steps, {
    showProgress: true,
    stagePadding: 8,
    overlayOpacity: 0.5,
    waitForElementsMs: 0,
    allowClose: true,

    // i18n labels
    finishBtnText: t(keys.FINISH_TUTORIAL), // Finish on last step
    firstStepNextText: t(keys.START), // GetStarted
    nextBtnText: t(keys.NEXT), // Next
    prevBtnText: t(keys.BACK), // Back
    // doneBtnText intentionally not passed -> keep native "X" icon

    onDestroyed: () => dispatch(setShouldRunSimulatorTour(false)),
    onCompleted: () => dispatch(setShouldRunSimulatorTour(false)),
  });

  // Keep app language stable while tour is open (prevents unexpected flip)
  const lockedLangRef = useRef(i18n.language);
  useEffect(() => {
    const onLangChange = (lng: string) => {
      if (shouldRunSimulatorTour && lng !== lockedLangRef.current) {
        Promise.resolve().then(() => i18n.changeLanguage(lockedLangRef.current));
      }
    };
    i18n.on('languageChanged', onLangChange);
    return () => i18n.off('languageChanged', onLangChange);
  }, [i18n, shouldRunSimulatorTour]);

  useEffect(() => {
    if (shouldRunSimulatorTour) {
      lockedLangRef.current = i18n.language; // lock language at open
      startTour();
    } else {
      stopTour();
    }
  }, [shouldRunSimulatorTour, startTour, stopTour, i18n.language]);

  const onCloseModal = () => setIsLineChartInModal(false);
  const handleOpenModal = () => setIsLineChartInModal(true);

  return (
    <Cnt>
      <WithSidebar>
        <SPageCnt>
          <LineEnergyProductionModal open={isLineChartInModal} onCloseModal={onCloseModal} />
          <div>
            <SWrapper>
              <FirstCol>
                <DataInputContext.Provider value={128}>
                  <CommonSimulatorHeader title={t(keys.PARAMETERS_SECTION_TITLE_SIMULATION)} />

                  <ScenarioWithExport>
                    <div className="choose-scenario" id={SIMULATOR_INTRO_STEPS.CHOOSE_SCENARIO}>
                      <ChooseScenarioCard />
                    </div>
                  </ScenarioWithExport>

                  <Spacer mb="20">
                    <SpecificInformationCard />
                  </Spacer>
                  <Box mb="20px" d="flex" justify="space-between" align="center">
                    <Button variant="outlined" to={URL.ADVANCED_OPTIONS} id={SIMULATOR_INTRO_STEPS.ADVANCED_DATA}>
                      {t(keys.ADVANCED_OPTIONS_LINK)}
                    </Button>
                    <SaveSimulation />
                  </Box>
                </DataInputContext.Provider>
              </FirstCol>

              <SecondCol>
                <SimulatorHeader />
                <Spacer mb="16px">
                  <div id={SIMULATOR_INTRO_STEPS.LINE_CHART}>
                    <LineEnergyProduction index={0} onOpenInModalMode={handleOpenModal} />
                  </div>
                </Spacer>
                <Spacer mb="16px">
                  <div id={SIMULATOR_INTRO_STEPS.ENERGY_BALANCE}>
                    <EnergyBalance legendExpanded />
                  </div>
                </Spacer>
                <div>
                  <EnergyPies />
                </div>
              </SecondCol>
            </SWrapper>

            <SPartnersCnt>
              <SPartnersBanner />
            </SPartnersCnt>
          </div>
        </SPageCnt>
      </WithSidebar>
    </Cnt>
  );
};
